import { isEmpty } from 'ramda';
import i18n from 'helpers/i18n';
import messages from './messages';
import { Dropdown, Input } from 'semantic-ui-react';
import { DIMENSIONS } from 'constants/dimensions';
import { Button } from '@teachstonellc/component-library';
import ToggleChips from './ToggleChips';

export interface FilterState {
  query: string;
  class_version: string[];
  age_level: string[];
  dimension: string[];
  language: string;
  resource_type: string[];
}

interface FilterSidebarProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
}

const DEFAULT_FILTERS: FilterState = {
  query: '',
  class_version: [],
  age_level: [],
  dimension: [],
  language: '',
  resource_type: []
};

export const CLASS_VERSION_OPTIONS = [
  { value: '2008', label: '2008' },
  { value: '2nd_edition', label: '2nd Edition' }
];

export const AGE_LEVEL_OPTIONS = [
  { value: 'infant', label: 'Infant' },
  { value: 'toddler', label: 'Toddler' },
  { value: 'pre-k', label: 'Pre-K' },
  { value: 'k-3', label: 'K-3' }
];

const DIMENSION_OPTIONS = Object.values(DIMENSIONS).map(d => ({
  key: d.abbreviation,
  value: d.t_name,
  text: d.name
}));

// URL-safe token whitelists consumed by urlState hydration.
export const CLASS_VERSION_VALUES = CLASS_VERSION_OPTIONS.map(o => o.value);
export const AGE_LEVEL_VALUES = AGE_LEVEL_OPTIONS.map(o => o.value);
export const DIMENSION_VALUES = DIMENSION_OPTIONS.map(o => o.value);

const LANGUAGE_OPTIONS = [
  { key: 'all', text: 'All', value: '' },
  { key: 'english', text: 'English', value: 'english' },
  { key: 'spanish', text: 'Spanish', value: 'spanish' }
];

const ResourceTypeOptions = [
  { key: 'video', text: 'Video Resources', value: 'video' },
  { key: 'non_video', text: 'Non-Video Resources', value: 'non_video' },
  { key: 'learning_series', text: 'Learning Series', value: 'learning_series' }
];

function hasActiveFilters(filters: FilterState): boolean {
  return (
    filters.query !== '' ||
    filters.class_version.length > 0 ||
    filters.age_level.length > 0 ||
    filters.dimension.length > 0 ||
    filters.language !== '' ||
    filters.resource_type?.length > 0
  );
}

function toggleItem(list: string[], item: string): string[] {
  return list.includes(item) ? list.filter(i => i !== item) : [...list, item];
}

function FilterSidebar({ filters, onFilterChange }: FilterSidebarProps) {
  function handleQueryChange(value: string) {
    onFilterChange({ ...filters, query: value });
  }
  return (
    <div className="pr-4">
      {/* Search */}
      <div className="mb-4">
        <label className="font-bold text-sm text-black block mb-1">
          {i18n.ft(messages.search)}
        </label>
        <Input
          placeholder={i18n.ft(messages.searchPlaceholder)}
          value={filters.query}
          onChange={(_, { value }) => handleQueryChange(value)}
          fluid
          size="small"
          icon={
            isEmpty(filters.query)
              ? 'search'
              : {
                  link: true,
                  name: 'times',
                  onClick: () => handleQueryChange('')
                }
          }
        />
      </div>

      {/* CLASS Version */}
      <div className="mb-4">
        <label className="font-bold text-sm text-black block mb-1">
          {i18n.ft(messages.classVersion)}
        </label>
        <ToggleChips
          options={CLASS_VERSION_OPTIONS}
          selected={filters.class_version}
          onToggle={v =>
            onFilterChange({
              ...filters,
              class_version: toggleItem(filters.class_version, v)
            })
          }
        />
      </div>

      {/* Age Level(s) */}
      <div className="mb-4">
        <label className="font-bold text-sm text-black block mb-1">
          {i18n.ft(messages.ageLevels)}
        </label>
        <ToggleChips
          options={AGE_LEVEL_OPTIONS}
          selected={filters.age_level}
          onToggle={v =>
            onFilterChange({
              ...filters,
              age_level: toggleItem(filters.age_level, v)
            })
          }
        />
      </div>

      {/* Dimension(s) */}
      <div className="mb-4 [&_.label]:!text-xs [&_.label]:!py-[0.3em] [&_.label]:!px-[0.5em] [&_.label]:!my-[0.15em] [&_.label]:!mx-[0.25em] [&_.label_.delete.icon]:!text-[0.65rem] [&_.label_.delete.icon]:!ml-[0.4em]">
        <label className="font-bold text-sm text-black block mb-1">
          {i18n.ft(messages.dimensions)}
        </label>

        <Dropdown
          placeholder={i18n.ft(messages.dimensionsPlaceholder)}
          fluid
          multiple
          selection
          search
          options={DIMENSION_OPTIONS}
          value={filters.dimension}
          onChange={(_, { value }) =>
            onFilterChange({ ...filters, dimension: value as string[] })
          }
          className="rounded-md"
        />
      </div>

      {/* Resource Type */}
      <div className="mb-4 [&_.label]:!text-xs [&_.label]:!py-[0.3em] [&_.label]:!px-[0.5em] [&_.label]:!my-[0.15em] [&_.label]:!mx-[0.25em] [&_.label_.delete.icon]:!text-[0.65rem] [&_.label_.delete.icon]:!ml-[0.4em]">
        <label className="font-bold text-sm text-black block mb-1">
          {i18n.ft(messages.resourceType)}
        </label>
        <Dropdown
          placeholder={i18n.ft(messages.resourceTypePlaceholder)}
          fluid
          multiple
          selection
          search
          options={ResourceTypeOptions}
          value={filters.resource_type}
          onChange={(_, { value }) =>
            onFilterChange({ ...filters, resource_type: value as string[] })
          }
          className="rounded-md"
        />
      </div>

      {/* Language */}
      <div className="mb-4">
        <label className="font-bold text-sm text-black block mb-1">
          {i18n.ft(messages.language)}
        </label>
        <Dropdown
          placeholder="All"
          fluid
          selection
          options={LANGUAGE_OPTIONS}
          value={filters.language}
          onChange={(_, { value }) =>
            onFilterChange({ ...filters, language: value as string })
          }
          className="rounded-md"
        />
      </div>

      {/* Clear Filters */}
      {hasActiveFilters(filters) && (
        <button
          onClick={() => onFilterChange(DEFAULT_FILTERS)}
          className="w-full text-sm text-[#0A9CCA] hover:text-[#077699] font-medium transition-colors py-1.5"
        >
          {i18n.ft(messages.clearFilters)}
        </button>
      )}
    </div>
  );
}

export default FilterSidebar;
